// The machine's heat-up as the API measured it from its cold starts (GET /api/machines/{id}/heatup): the weekly plan
// shows ready times and the machine switches on this much earlier (Dominik 21.10.2026, core/schedule.ts). Fetched when
// a screen first asks and again every 30 min; until the first answer (or without one) the default lead applies.
import type { HeatupResponse } from '@brewcore/app-contract';
import { jsonRequest, type HttpTarget } from './http.js';
import type { HeatupInfo } from './screens/context.js';

export const HEATUP_REFRESH_MS = 30 * 60_000;

export class HeatupStore {
  private info: HeatupInfo | null = null;
  private fetchedAt = 0;
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly machineId: string,
    private readonly target: HttpTarget,
    private readonly now: () => number = Date.now,
  ) {}

  private timer: ReturnType<typeof setInterval> | null = null;

  /** The first subscriber fetches; while anyone listens, the answer is renewed every HEATUP_REFRESH_MS. */
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    void this.refresh();
    this.timer ??= setInterval(() => void this.refresh(), HEATUP_REFRESH_MS);
    return () => {
      this.listeners.delete(fn);
      if (this.listeners.size === 0 && this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  };

  getSnapshot = (): HeatupInfo | null => this.info;

  /**
   * Fetches unless an answer is younger than HEATUP_REFRESH_MS; while a fetch runs, a second call waits for it. A failed
   * fetch keeps the last answer.
   */
  refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this.fetchedAt && this.now() - this.fetchedAt < HEATUP_REFRESH_MS) return Promise.resolve();
    this.inFlight = this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    const r = await jsonRequest<HeatupResponse>(
      this.target,
      'GET',
      `/api/machines/${this.machineId}/heatup`,
      null,
      'http-heatup-response',
    );
    if (!r.ok) return;
    this.fetchedAt = this.now();
    this.info = { seconds: r.value.seconds, samples: r.value.samples };
    for (const fn of this.listeners) fn();
  }
}
